import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import { zipSync } from "fflate"

// Bulk actions on the invoices ticked in the table: download their PDFs as one
// zip, or file them with FBR.
//
// Both run one request per invoice from the browser instead of one server request
// for the lot. A PDF is a headless-Chrome render and an upload is an FBR round
// trip — a few seconds each — so a page of either would outlast the 30s the proxy
// allows a request. Going one at a time also lets the dialog show which invoice
// is processing and which are completed (bulk-progress), and a Stop keeps
// everything already done.
export default class extends Controller {
  static outlets = ["invoice-selection", "bulk-progress"]
  static targets = [
    "button", "count", "menu", "hint", "pdfItem", "pdfCount", "uploadItem", "uploadCount",
    "dialog", "title", "summary", "cancelButton", "stopButton", "confirmButton", "doneButton"
  ]
  static values = { environment: String, zipName: String }

  connect() {
    this.running = false
    this.refresh()
  }

  disconnect() {
    this.stopRequested = true
  }

  invoiceSelectionOutletConnected() {
    this.refresh()
  }

  // ── Menu ──────────────────────────────────────────────────────────────────

  refresh() {
    const selected   = this.selected()
    const uploadable = selected.filter((invoice) => invoice.uploadable).length

    this.countTarget.hidden      = selected.length === 0
    this.countTarget.textContent = selected.length

    this.pdfItemTarget.disabled        = selected.length === 0
    this.pdfCountTarget.textContent    = selected.length || ""
    this.uploadItemTarget.disabled     = uploadable === 0
    this.uploadCountTarget.textContent = uploadable || ""

    if (selected.length === 0) {
      this.hintTarget.textContent = "Tick invoices in the list to act on several at once."
    } else if (uploadable === 0) {
      this.hintTarget.textContent = "The ticked invoices are already uploaded or archived, so there is nothing to file."
    }
    this.hintTarget.hidden = selected.length > 0 && uploadable > 0
  }

  toggleMenu() {
    this.menuTarget.hidden ? this.openMenu() : this.closeMenu()
  }

  openMenu() {
    this.refresh()
    this.menuTarget.hidden = false
    this.buttonTarget.setAttribute("aria-expanded", "true")
  }

  closeMenu() {
    this.menuTarget.hidden = true
    this.buttonTarget.setAttribute("aria-expanded", "false")
  }

  closeMenuOnOutsideClick(event) {
    if (this.menuTarget.hidden || this.element.contains(event.target)) return
    this.closeMenu()
  }

  // ── Download PDFs ─────────────────────────────────────────────────────────

  async downloadPdfs() {
    const invoices = this.selected()
    if (invoices.length === 0 || this.running) return
    this.closeMenu()

    this.openDialog(
      "Downloading PDFs",
      `Rendering ${plural(invoices.length, "PDF")}. Keep this tab open until the download starts.`
    )

    const files = {}
    const attempted = await this.process(invoices, "Rendering PDF…", async (invoice) => {
      const response = await fetch(invoice.pdfUrl, { headers: { Accept: "application/pdf" } })
      if (!response.ok) throw new Error(`Could not render the PDF (error ${response.status}).`)
      if (!(response.headers.get("content-type") || "").includes("application/pdf")) {
        throw new Error("Unexpected response — refresh the page and try again.")
      }
      const name = uniqueFileName(files, invoice.number)
      files[name] = new Uint8Array(await response.arrayBuffer())
      return { state: "success", message: `Ready · ${name}` }
    })

    const names = Object.keys(files)
    const parts = []
    if (names.length === 1 && invoices.length === 1) {
      saveFile(new Blob([files[names[0]]], { type: "application/pdf" }), names[0])
      parts.push(`Saved ${names[0]}`)
    } else if (names.length > 0) {
      // Stored, not deflated: the PDFs are compressed inside already, and
      // deflating them again would only cost time for a few bytes.
      saveFile(new Blob([zipSync(files, { level: 0 })], { type: "application/zip" }), this.zipNameValue)
      parts.push(`Saved ${this.zipNameValue} with ${plural(names.length, "PDF")}`)
    }
    if (attempted > names.length)     parts.push(`${attempted - names.length} could not be rendered`)
    if (invoices.length > attempted)  parts.push(`${invoices.length - attempted} not rendered`)

    this.titleTarget.textContent   = names.length > 0 ? "PDFs downloaded" : "No PDFs downloaded"
    this.summaryTarget.textContent = `${parts.join(" · ")}.`
    this.showButtons("done")
  }

  // ── Upload to FBR ─────────────────────────────────────────────────────────

  confirmUpload() {
    if (this.running) return
    const selected   = this.selected()
    const uploadable = selected.filter((invoice) => invoice.uploadable)
    if (uploadable.length === 0) return
    this.closeMenu()

    // Filed oldest first (the table lists newest first): a debit note ticked along
    // with the sale invoice it references can only be filed once that has its IRN.
    this.pendingUpload = uploadable.reverse()

    const destination = this.environmentValue === "Production" ? "FBR" : "the FBR sandbox"
    const skipped     = selected.length - uploadable.length
    let summary = `File ${plural(uploadable.length, "invoice")} with ${destination}? ` +
                  "Once filed, they cannot be edited or deleted."
    if (skipped > 0) {
      summary += ` ${plural(skipped, "ticked invoice")} ${skipped === 1 ? "is" : "are"} already uploaded or archived and will be skipped.`
    }

    this.openDialog("Upload to FBR", summary)
    this.confirmButtonTarget.textContent = `Upload ${plural(uploadable.length, "invoice")}`
    this.showButtons("cancel", "confirm")
  }

  async startUpload() {
    const invoices = this.pendingUpload
    if (!invoices || this.running) return
    this.pendingUpload = null
    // Statuses in the table are stale once anything has been sent.
    this.reloadOnClose = true

    this.titleTarget.textContent   = "Uploading to FBR"
    this.summaryTarget.textContent = "Keep this tab open. Invoices are filed one at a time — stopping keeps the ones already filed."

    const tally = { uploaded: 0, rejected: 0, notSent: 0 }
    const attempted = await this.process(invoices, "Filing with FBR…", async (invoice) => {
      const result = await this.upload(invoice)
      if (result.outcome === "uploaded")      tally.uploaded++
      else if (result.outcome === "rejected") tally.rejected++
      else                                    tally.notSent++
      return result
    })

    const parts = []
    if (tally.uploaded) parts.push(`${tally.uploaded} uploaded`)
    if (tally.rejected) parts.push(`${tally.rejected} rejected by FBR`)
    if (tally.notSent)  parts.push(`${tally.notSent} not sent`)
    if (invoices.length > attempted) parts.push(`${invoices.length - attempted} not attempted`)

    this.titleTarget.textContent   = invoices.length > attempted ? "Upload stopped" : "Upload finished"
    this.summaryTarget.textContent = `${parts.join(" · ")}.`
    this.showButtons("done")
  }

  // One invoice through upload_to_fbr.json, resolved to the action's outcome plus
  // the bulk-progress state that shows it. Anything other than the action's own
  // JSON — Devise's 401 for an expired session, a redirect to the plans page, a
  // 500, a dropped connection — halts the run: filing cannot be undone, so an
  // unexplained failure is a reason to stop and look rather than press on.
  async upload(invoice) {
    let response, data
    try {
      response = await fetch(invoice.uploadUrl, {
        method:  "POST",
        headers: { Accept: "application/json", "X-CSRF-Token": csrfToken() }
      })
      if ((response.headers.get("content-type") || "").includes("application/json")) {
        data = await response.json()
      }
    } catch {
      return { outcome: "error", state: "failure", message: "Connection lost. Refresh the page to see this invoice's status.", halt: true }
    }

    switch (data?.result) {
      case "uploaded":
        return { outcome: "uploaded", state: "success", message: data.fbr_irn ? `Filed · IRN ${data.fbr_irn}` : data.message }
      case "rejected":
        return { outcome: "rejected", state: "failure", message: data.message }
      case "blocked":
        return { outcome: "blocked", state: "warning", message: data.message, halt: data.halt }
      case "stock_warning":
        return { outcome: "stock_warning", state: "warning", message: `${data.message}. Open the invoice to upload anyway.` }
      default:
        return {
          outcome: "error",
          state:   "failure",
          message: data?.error || `Unexpected response (${response.status}). Refresh the page and try again.`,
          halt:    true
        }
    }
  }

  // ── Dialog ────────────────────────────────────────────────────────────────

  // Works through the invoices one at a time. bulk-progress lists them all as
  // waiting, then marks each processing (with `activity` as its message) and
  // completed as it goes. `step` resolves to { state, message, halt }; a throw is
  // shown as a failure. Returns how many invoices were attempted before the run
  // ended, which is fewer than all of them after a Stop or a halt.
  async process(invoices, activity, step) {
    const progress = this.bulkProgressOutlet
    this.running       = true
    this.stopRequested = false
    this.stopButtonTarget.disabled = false
    this.showButtons("stop")
    progress.start(invoices.map((invoice) => ({ key: invoice.id, label: `#${invoice.number}` })))

    let attempted = 0
    for (const invoice of invoices) {
      if (this.stopRequested) break
      progress.processing(invoice.id, activity)

      let result
      try {
        result = await step(invoice)
      } catch (error) {
        result = { state: "failure", message: error.message }
      }
      attempted++
      progress.complete(invoice.id, result.state, result.message)
      if (result.halt) break
    }

    progress.finish()
    this.running = false
    return attempted
  }

  openDialog(title, summary) {
    this.titleTarget.textContent   = title
    this.summaryTarget.textContent = summary
    this.bulkProgressOutlet.hide()
    this.reloadOnClose = false
    if (!this.dialogTarget.open) this.dialogTarget.showModal()
  }

  showButtons(...names) {
    const buttons = {
      cancel:  this.cancelButtonTarget,
      stop:    this.stopButtonTarget,
      confirm: this.confirmButtonTarget,
      done:    this.doneButtonTarget
    }
    Object.entries(buttons).forEach(([name, button]) => { button.hidden = !names.includes(name) })
  }

  stop() {
    this.stopRequested = true
    this.stopButtonTarget.disabled = true
    this.summaryTarget.textContent = "Stopping once the invoice in progress completes…"
  }

  closeDialog() {
    if (!this.running) this.dialogTarget.close()
  }

  // Esc must not close the dialog mid-run and leave requests going unseen.
  guardCancel(event) {
    if (this.running) event.preventDefault()
  }

  dialogClosed() {
    // Chrome lets a second Esc through even when cancel is prevented.
    if (this.running) {
      this.dialogTarget.showModal()
      return
    }
    this.pendingUpload = null
    if (this.reloadOnClose) {
      this.reloadOnClose = false
      Turbo.visit(window.location.href, { action: "replace" })
    }
  }

  selected() {
    return this.hasInvoiceSelectionOutlet ? this.invoiceSelectionOutlet.selected : []
  }
}

function plural(count, noun) {
  return `${count} ${noun}${count === 1 ? "" : "s"}`
}

function csrfToken() {
  return document.querySelector("meta[name='csrf-token']")?.content || ""
}

// Invoice numbers can hold characters a file name cannot (INV/2026/001), and
// replacing those can make two numbers collide — so suffix any repeat.
function uniqueFileName(files, number) {
  const base = String(number || "").replace(/[\\/:*?"<>| -]+/g, "-").trim() || "invoice"
  let name = `${base}.pdf`
  for (let n = 2; name in files; n++) name = `${base}-${n}.pdf`
  return name
}

function saveFile(blob, filename) {
  const url  = URL.createObjectURL(blob)
  const link = Object.assign(document.createElement("a"), { href: url, download: filename })
  link.dataset.turbo = "false"
  document.body.append(link)
  link.click()
  link.remove()
  // Some browsers read the blob after click() returns, so it is not revoked at once.
  setTimeout(() => URL.revokeObjectURL(url), 60_000)
}
